import * as React from 'react'

import {AuthClass} from '@aws-amplify/auth/lib-esm/Auth'
import {FormikErrors, useFormik} from 'formik'
import invariant from 'invariant'
import isEmpty from 'lodash/isEmpty'
import {ConnectedProps} from 'react-redux'
import {useLocation} from 'react-router-dom'
import {replace} from 'redux-first-history'

import * as authActions from '../../actions/auth'
import * as navigationActions from '../../actions/navigation'
import {State} from '../../reducers'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import * as validator from '../../../../commons/src/libs/validator'
import createCognitoStorage from '../../../../commons/src/services/cognito-storage'
import {connect} from '../../../../commons/src/services/loop'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import {isAdminModePermission} from '../../libs/user'
import * as providerApi from '../../services/api/provider'
import * as userApi from '../../services/api/user'
import authService from '../../services/auth'

import {ExtendedCognitoUserWithPermission, ProviderId} from '../../types'

import signOutIcon from '../../../../commons/src/assets/icons/sign-out.svg'

import Button from '../../../../commons/src/components/button'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'

import AuthLayoutPartial from './auth-layout-partial'

const mapStateToProps = (state: State) => ({
  authUser: state.auth.user,
  isAdminMode: state.auth.isAdminMode,
  provider: state.core.provider,
})

const mapDispatchToProps = {
  signIn: authActions.signIn,
  replaceRoute: (path: string) => replace(path),
  goToIndex: navigationActions.goToIndex,
  signOut: authActions.signOut,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const SignInAdminPage = ({
  isAdminMode,
  authUser,
  provider,
  signIn,
  replaceRoute,
  goToIndex,
  signOut,
}: Props) => {
  const location = useLocation()
  const locationState = location.state as undefined | {from?: string}

  const [user, setUser] = React.useState<ExtendedCognitoUserWithPermission>()
  const [providerOptions, setProviderOptions] = React.useState<
    {value: ProviderId; label: string}[]
  >([])
  const [selectedProviderId, setSelectedProviderId] = React.useState<ProviderId>()

  const fetchProviderOptions = async () => {
    const providers = await providerApi.get()
    const options: {value: ProviderId; label: string}[] = providers.map(p => ({
      value: p.id,
      label: p.name,
    }))
    const sortedOptions = options.sort((a, b) => {
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    })
    setProviderOptions(sortedOptions)
  }

  React.useEffect(() => {
    // Set auth user and show provider selection options
    if (authUser && isAdminMode && !provider) {
      setUser(authUser)
      fetchProviderOptions()
      return
    }

    // Redirect if user is authenticated and selected a provider
    if (authUser && isAdminMode && !!provider) {
      if (locationState?.from) {
        replaceRoute(locationState.from)
      } else {
        goToIndex(replace)
      }
    }
  }, [isAdminMode, authUser, provider])

  const getUserWithPermissions = async (currentAuth: AuthClass) => {
    const authenticatedUser = await currentAuth.currentAuthenticatedUser()
    const userId = authenticatedUser.attributes.sub
    const {permissions} = await userApi.getUserPermissions(userId)
    const newUser = {
      ...authenticatedUser,
      permission: permissions,
    }
    return newUser
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      // Additional errors
      notAuthorized: false,
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.combineValidators(validator.required, validator.email)(values.email)) {
        errors.email = 'true'
      }

      if (!validator.required(values.password)) {
        errors.password = 'true'
      }

      return errors
    },
    onSubmit: (values, actions) => {
      return authService
        .getAuth()
        .then(async auth => {
          auth.configure({storage: createCognitoStorage()})
          const signInUser = await auth.signIn(values.email, values.password)
          return [signInUser, auth]
        })
        .then(async ([signInUser, auth]) => {
          if (signInUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
            actions.setFieldError('general', 'Something is wrong, please contact the support team.')
          } else {
            const userWithPermissions = await getUserWithPermissions(auth)

            if (isAdminModePermission(userWithPermissions.permission)) {
              setUser(userWithPermissions)
              formik.resetForm()
              fetchProviderOptions()
            }
            actions.setFieldError('notAuthorized', 'true')
          }
        })
        .catch(error => {
          if (error.name === 'NotAuthorizedException') {
            actions.setFieldError('notAuthorized', 'true')
          } else {
            actions.setFieldError('general', error.message)
          }
        })
    },
  })

  const handleSignIn = () => {
    invariant(user, 'User must be authenticated to access admin page')
    invariant(selectedProviderId, 'Missing provider ID')
    signIn(user, selectedProviderId)
  }

  useKeyPress(
    preventDoubleClick(() => {
      formik.handleSubmit()
    }),
    'Enter'
  )

  return (
    <AuthLayoutPartial>
      {!user && (
        <>
          <Headline label="Partner Portal Admin" classNames={['u-align-center']} />
          <form onSubmit={formik.handleSubmit}>
            {formik.errors.general && <Paragraph error>{formik.errors.general}</Paragraph>}
            {formik.errors.notAuthorized && (
              <Paragraph error>
                We can&apos;t find an account matching your sign in information or the provided
                password is wrong.
              </Paragraph>
            )}
            <FormRow>
              <InputField
                name="email"
                type="email"
                label="E-Mail"
                autoFocus
                error={formik.submitCount > 0 && !!formik.errors.email}
                onChange={(v, n, event) => formik.handleChange(event)}
                value={formik.values.email}
                onBlur={formik.handleBlur}
              />
            </FormRow>
            <FormRow>
              <InputField
                type="password"
                label="Password"
                name="password"
                showPassword
                error={formik.submitCount > 0 && !!formik.errors.password}
                onChange={(v, n, event) => formik.handleChange(event)}
                value={formik.values.password}
                onBlur={formik.handleBlur}
              />
            </FormRow>
          </form>
          <Button
            as="a"
            block
            disabled={formik.isSubmitting || (!isEmpty(formik.errors) && formik.submitCount > 0)}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Continue
          </Button>
        </>
      )}
      {user && (
        <Stack space="400">
          <Headline label="Welcome Admin" classNames={['u-align-center', 'u-no-margin-bottom']} />
          <Stack space="200">
            <Paragraph classNames={['u-align-center', 'u-no-margin-bottom']}>
              Please select a provider:
            </Paragraph>
            <SelectField
              placeholder="Provider"
              name="adminProvider"
              menu={<SelectMenu values={providerOptions} />}
              value={providerOptions.find(({value}) => value === selectedProviderId)}
              onChange={(value, name = '') => setSelectedProviderId(value.value)}
            />
          </Stack>
          <SplitLayout
            verticalAlignment="start"
            left={
              <Button
                hierarchy="secondary"
                onClick={() => {
                  setUser(undefined)
                  signOut()
                }}
                icon={<Icon source={signOutIcon} />}
              >
                Sign out
              </Button>
            }
            right={
              <Button onClick={handleSignIn} disabled={!selectedProviderId}>
                Proceed
              </Button>
            }
          />
        </Stack>
      )}
    </AuthLayoutPartial>
  )
}

export default connector(SignInAdminPage)
